import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, Spinner } from "@phosphor-icons/react";

export default function PaymentResult() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshCompany } = useAuth();
  const sessionId = params.get("session_id");
  const isCancel = window.location.pathname.includes("cancel");
  const [status, setStatus] = useState(isCancel ? "cancelled" : "checking");
  const attempts = useRef(0);

  useEffect(() => {
    if (isCancel || !sessionId) return;
    const poll = async () => {
      if (attempts.current >= 8) { setStatus("timeout"); return; }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        if (data.payment_status === "paid") { setStatus("paid"); refreshCompany(); return; }
        if (data.status === "expired" || data.payment_status === "failed") { setStatus("failed"); return; }
      } catch (_) {}
      setTimeout(poll, 2000);
    };
    poll();
    // eslint-disable-next-line
  }, []);

  const M = {
    checking: { icon: <Spinner size={56} className="animate-spin text-emerald-600" />, title: "Confirming your payment…", desc: "This will only take a moment." },
    paid: { icon: <CheckCircle size={56} weight="fill" className="text-emerald-500" />, title: "Payment successful!", desc: "Your subscription has been upgraded." },
    cancelled: { icon: <XCircle size={56} weight="fill" className="text-zinc-400" />, title: "Checkout cancelled", desc: "No charge was made. You can pick a plan anytime." },
    failed: { icon: <XCircle size={56} weight="fill" className="text-red-500" />, title: "Payment failed", desc: "Something went wrong. Please try again." },
    timeout: { icon: <Spinner size={56} className="text-amber-500" />, title: "Still processing", desc: "Your payment is taking longer than expected. Check billing history shortly." },
  }[status];

  return (
    <div className="min-h-[70vh] flex items-center justify-center p-6" data-testid="payment-result">
      <div className="text-center max-w-md">
        <div className="flex justify-center mb-5">{M.icon}</div>
        <h1 className="font-display text-2xl font-bold tracking-tight">{M.title}</h1>
        <p className="text-zinc-500 mt-2">{M.desc}</p>
        <div className="flex gap-3 justify-center mt-8">
          <Button onClick={() => navigate("/app/billing")} variant="outline" className="h-11 border-zinc-300" data-testid="back-billing">Back to Billing</Button>
          <Button onClick={() => navigate("/app/dashboard")} className="h-11 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold" data-testid="to-dashboard">Go to Dashboard</Button>
        </div>
      </div>
    </div>
  );
}
